import React, { Children } from 'react';

import { SortingProps, Table, TableRenderProps } from '@block-explorer/ui';
import { TableRowType } from '@make-software/cspr-design';

import TableSettings, {
  PaginationProps,
  useClientPagination,
} from '../table-settings/table-settings';
import { TableLoader } from './table-loader';
import { NoDataFoundTableError, TableError } from './table-error';
import type { TableViewId } from '../../redux/table-view';

/** @description use paginationStateKey for client table-settings or use use paginationProps for API table-settings*/
type PaginationType =
  | { clientPaginationStateKey: string }
  | { paginationProps: PaginationProps; sortingProps: SortingProps };

type RenderProps = TableRenderProps & { sortingProps: SortingProps };

export type PageDataTableProps<Entity> = {
  loading: boolean;
  error?: string | null;
  data: null | Entity[];
  noDataError?: boolean;
  renderDataHeader: (renderProps: RenderProps) => JSX.Element;
  renderPaginatedData: (
    paginatedData: Entity[],
    renderProps: RenderProps
  ) => JSX.Element | JSX.Element[];
  renderTopActions?: () => JSX.Element | JSX.Element[];
  renderTopCheckbox?: () => JSX.Element;
  tableRowType?: TableRowType;
  hideTopTotalCount?: boolean;
  tableId?: TableViewId;
  totalRowsLabel?: string;
  defaultPageSize?: number;
} & PaginationType;

export const PageDataTable = <Entity extends any>({
  loading,
  data,
  noDataError,
  renderDataHeader,
  renderPaginatedData,
  renderTopActions,
  renderTopCheckbox,
  clientPaginationStateKey,
  paginationProps,
  sortingProps,
  error,
  tableRowType = TableRowType.TextWithAvatar,
  hideTopTotalCount = false,
  tableId,
  totalRowsLabel,
  defaultPageSize,
  ...props
}: (PageDataTableProps<Entity> & {
  clientPaginationStateKey?: string;
}) & {
  paginationProps?: PaginationProps;
  sortingProps?: SortingProps;
}) => {
  let tablePaginationData = data;
  let tablePaginationProps = paginationProps;
  let tableSortingProps = sortingProps;

  const {
    data: clientPaginationData,
    paginationProps: clientPaginationProps,
    sortingProps: clientSortingProps,
  } = useClientPagination({
    paginationStateKey: clientPaginationStateKey || '',
    arr: tablePaginationData,
    requestParams: { defaultPageSize },
  });

  // when tablePaginationProps is null means it's client side table-settings
  if (tablePaginationProps == null) {
    tablePaginationData = clientPaginationData;
    tablePaginationProps = clientPaginationProps;
    tableSortingProps = clientSortingProps;
  }

  const renderTopPagination = () => (
    <TableSettings
      hideTotalCount={hideTopTotalCount}
      hideRowsPerPage
      tableId={tableId}
      actions={renderTopActions?.()}
      checkbox={renderTopCheckbox?.()}
      {...tablePaginationProps!}
    />
  );
  const renderBottomPagination = () => (
    <TableSettings totalRowsLabel={totalRowsLabel} {...tablePaginationProps!} />
  );

  return (
    <Table
      renderDataHeaders={(renderProps) =>
        renderDataHeader({ ...renderProps, sortingProps: tableSortingProps! })
      }
      renderData={(renderProps) =>
        tablePaginationData == null || loading ? (
          <TableLoader
            columnsLength={Children.count(
              renderDataHeader({
                ...renderProps,
                sortingProps: tableSortingProps!,
              }).props.children
            )}
            rowsLength={tablePaginationProps!.perPage}
            tableRowType={tableRowType}
          />
        ) : error ? (
          <TableError
            columnsLength={Children.count(
              renderDataHeader({
                ...renderProps,
                sortingProps: tableSortingProps!,
              }).props.children
            )}
          />
        ) : noDataError ? (
          <NoDataFoundTableError
            columnsLength={Children.count(
              renderDataHeader({
                ...renderProps,
                sortingProps: tableSortingProps!,
              }).props.children
            )}
          />
        ) : (
          renderPaginatedData(tablePaginationData, {
            ...renderProps,
            sortingProps: tableSortingProps!,
          })
        )
      }
      renderHeader={renderTopPagination}
      renderFooter={renderBottomPagination}
      {...props}
    />
  );
};

export default PageDataTable;
